import { MAX_ISO_WEEK, TRACK_SCALE_OF_TARGET } from '../constants/overviewConstants';
import type {
  OverviewLocation,
  OverviewLocationDto,
  OverviewMetric,
  OverviewMetricDto,
  OverviewSeats,
  OverviewSeatsDto
} from '../types/overviewTypes';

// A metric known to have a ceiling. buildMetricView takes this rather than a
// bare OverviewMetric so the no-target case cannot reach it: the view used to
// answer that case with target: 0, which is exactly the collapse the nullable
// target exists to prevent, reintroduced one function later.
export type TargetedMetric = Omit<OverviewMetric, 'target'> & { target: number };

// Presentation view of one metric. These are display derivations of fields the
// endpoint already aggregated (adding two returned numbers, converting a value
// to a track position) — NOT aggregation over plan data, which stays server-side.
export interface MetricView {
  target: number;
  potential: number;
  realised: number;
  // Potential planned into weeks that have already started and did not land.
  // Encoding B draws realised as a caret rather than a segment, so this gap is
  // stated as a figure instead of being left as a subtraction for the reader.
  shortfall: number;
  // Potential above the ceiling. Earns nothing.
  excess: number;
  roomToTarget: number;
  lockedPotential: number;
  openPotential: number;
  isOverTarget: boolean;
  // Track positions, as percentages of a track running to TRACK_SCALE_OF_TARGET
  // times the target.
  lockedPercent: number;
  openPercent: number;
  excessPercent: number;
  realisedPercent: number;
}

// Without a target there is no scale and no bar: the track's whole meaning is
// distance from a ceiling. A type predicate rather than a plain boolean, so
// narrowing on it is what lets a caller reach buildMetricView at all.
//
// Finiteness and positivity rather than a null check: the REH-213 client is
// generated from OpenAPI, so an absent target arrives as `undefined` rather than
// null, and NaN or a negative ceiling would pass a null check and then be
// divided by. Anything that is not a usable ceiling reads as "no target", which
// is a state the card already draws honestly.
export function hasTarget(metric: OverviewMetric): metric is TargetedMetric {
  const { target } = metric;
  return typeof target === 'number' && Number.isFinite(target) && target > 0;
}

// A count of money or seats: never negative, never NaN, and absent reads as
// nothing rather than as a hole further down.
function toCount(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

// 0 is the "nothing locked yet" sentinel the legend already understands, so
// anything unusable — absent, negative, NaN, past the end of an ISO year —
// lands there rather than printing as a week number.
function toWeekNumber(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  const week = Math.trunc(value);
  return week > 0 && week <= MAX_ISO_WEEK ? week : 0;
}

function toMetric(dto: OverviewMetricDto | undefined): OverviewMetric {
  const target = dto?.target;
  return {
    // Only a usable ceiling survives: everything else is "no target", which the
    // card draws as a state of its own rather than as a bar against a guess.
    target: typeof target === 'number' && Number.isFinite(target) && target > 0 ? target : null,
    lockedPotential: toCount(dto?.lockedPotential),
    openPotential: toCount(dto?.openPotential),
    realised: toCount(dto?.realised)
  };
}

function toSeats(dto: OverviewSeatsDto | undefined): OverviewSeats {
  return { planned: toCount(dto?.planned), completed: toCount(dto?.completed) };
}

/**
 * The overview's data boundary: one endpoint row in, one trusted location out.
 *
 * Everything the components read passes through here, which is the point. The
 * REH-213 client will be generated from OpenAPI and will therefore type every
 * figure as possibly absent; rather than each card asking whether its number is
 * a number, the question is answered once, here, and answered the way the design
 * already has states for — a missing ceiling is "no target", a missing figure is
 * zero, a nonsense week is "nothing locked".
 *
 * Null for a row with no location id: figures with nothing to attach them to
 * cannot be shown against a location, and inventing one would put another
 * centre's money on the card.
 */
export function toOverviewLocation(dto: OverviewLocationDto): OverviewLocation | null {
  if (!dto.locationId) return null;

  const lockedFromWeek = toWeekNumber(dto.lockedFromWeek);
  const lockedToWeek = toWeekNumber(dto.lockedToWeek);
  // An inverted or half-missing range is not a range. Both ends drop to the
  // sentinel together, so the legend never names "weeks 34-0".
  const isLockedRange = lockedFromWeek > 0 && lockedToWeek >= lockedFromWeek;

  return {
    locationId: dto.locationId,
    hasPlan: dto.hasPlan === true,
    lockedFromWeek: isLockedRange ? lockedFromWeek : 0,
    lockedToWeek: isLockedRange ? lockedToWeek : 0,
    money: toMetric(dto.money),
    seats: toSeats(dto.seats),
    completionDependentMoney: toCount(dto.completionDependentMoney)
  };
}

export function getPotential(metric: OverviewMetric): number {
  return metric.lockedPotential + metric.openPotential;
}

export function buildMetricView(metric: TargetedMetric): MetricView {
  const { target } = metric;
  const potential = getPotential(metric);
  const shortfall = Math.max(0, metric.lockedPotential - metric.realised);

  const trackMax = target * TRACK_SCALE_OF_TARGET;
  // Clamped at the track: potential far above the ceiling, or a realised figure
  // inflated by per-patient overrides, would otherwise position the caret and
  // the excess segment outside the bar entirely.
  const toPercent = (value: number): number =>
    trackMax > 0 ? Math.min(100, (value / trackMax) * 100) : 0;

  // Segments are clipped at the target rule and the overflow drawn beyond it, so
  // locked potential that on its own exceeds the ceiling still reads correctly
  // rather than pushing the open segment past the rule.
  const lockedDrawn = Math.min(metric.lockedPotential, target);
  const openDrawn = Math.max(0, Math.min(metric.openPotential, target - lockedDrawn));
  const excess = Math.max(0, potential - target);

  return {
    target,
    potential,
    realised: metric.realised,
    shortfall,
    excess,
    roomToTarget: Math.max(0, target - potential),
    lockedPotential: metric.lockedPotential,
    openPotential: metric.openPotential,
    isOverTarget: excess > 0,
    lockedPercent: toPercent(lockedDrawn),
    openPercent: toPercent(openDrawn),
    excessPercent: toPercent(excess),
    realisedPercent: toPercent(metric.realised)
  };
}
